/**
 * Shared option arrays for select and numberCombo fields
 *
 * These options are based on @segmently/schema-models types
 * and are reused across all editor sections.
 */

/**
 * Font Family options from FontFamily enum
 */
export const FONT_FAMILY_OPTIONS = [
  // Sans-serif
  { value: 'SF Pro', label: 'SF Pro' },
  { value: 'SF ProRounded', label: 'SF ProRounded' },
  { value: 'Helvetica Neue', label: 'Helvetica Neue' },
  { value: 'Avenir Next', label: 'Avenir Next' },
  // Monospace
  { value: 'SF Mono', label: 'SF Mono' },
  // Serif
  { value: 'New York', label: 'New York' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Baskerville', label: 'Baskerville' },
] as const;

/**
 * Font Weight options (100-900)
 */
export const FONT_WEIGHT_OPTIONS = [
  { value: '100', label: '100 - Thin' },
  { value: '200', label: '200 - Extra Light' },
  { value: '300', label: '300 - Light' },
  { value: '400', label: '400 - Regular' },
  { value: '500', label: '500 - Medium' },
  { value: '600', label: '600 - Semi Bold' },
  { value: '700', label: '700 - Bold' },
  { value: '800', label: '800 - Extra Bold' },
  { value: '900', label: '900 - Black' },
] as const;

/**
 * Font Weight options (short labels)
 */
export const FONT_WEIGHT_OPTIONS_SHORT = [
  { value: '100', label: '100' },
  { value: '200', label: '200' },
  { value: '300', label: '300' },
  { value: '400', label: '400' },
  { value: '500', label: '500' },
  { value: '600', label: '600' },
  { value: '700', label: '700' },
  { value: '800', label: '800' },
  { value: '900', label: '900' },
] as const;

/**
 * Text Align options from LabelPosition enum
 */
export const TEXT_ALIGN_OPTIONS = [
  { value: 'left', label: 'left' },
  { value: 'center', label: 'center' },
  { value: 'right', label: 'right' },
] as const;

/**
 * V2 Text Alignment options using TextAlignment enum (start/center/end)
 */
export const V2_TEXT_ALIGN_OPTIONS = [
  { value: 'start', label: 'left' },
  { value: 'center', label: 'center' },
  { value: 'end', label: 'right' },
] as const;

/**
 * Horizontal Alignment options for section content (HorizontalAlignment enum)
 * RTL-aware: start = left in LTR, end = right in LTR
 */
export const HORIZONTAL_ALIGNMENT_OPTIONS = [
  { value: 'start', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'end', label: 'Right' },
] as const;

/**
 * Number options 0-50 for fontSize, lineHeight etc
 */
export const NUMBER_0_50_OPTIONS = Array.from({ length: 51 }, (_, i) => ({
  value: String(i),
  label: String(i),
}));

/**
 * Opacity options 0.0-1.0 step 0.1 for backgroundColorOpacity etc
 */
export const OPACITY_OPTIONS = Array.from({ length: 11 }, (_, i) => ({
  value: String(+(i / 10).toFixed(1)),
  label: String(+(i / 10).toFixed(1)),
}));

/**
 * Scale Mode options for images/videos
 */
export const SCALE_MODE_OPTIONS = [
  { value: 'scaleToFill', label: 'Scale to Fill' },
  { value: 'scaleAspectFit', label: 'Aspect Fit' },
  { value: 'scaleAspectFill', label: 'Aspect Fill' },
  { value: 'center', label: 'Center' },
  { value: 'top', label: 'Top' },
  { value: 'bottom', label: 'Bottom' },
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
] as const;

/**
 * Boolean as select options
 */
export const BOOLEAN_OPTIONS = [
  { value: 'true', label: 'true' },
  { value: 'false', label: 'false' },
] as const;

/**
 * Checkbox Kind options from CheckBoxKind enum
 * Controls the shape of checkboxes in list items
 */
export const CHECKBOX_KIND_OPTIONS = [
  { value: 'square', label: 'Square' },
  { value: 'circle', label: 'Circle' },
] as const;

/**
 * TextKind options for template text mode selector
 * From TextKind enum in @segmently/schema-models
 */
export const TEXT_KIND_OPTIONS = [
  { value: 'default', label: 'default' },
  { value: 'template', label: 'template' },
] as const;

/**
 * Link type options for template text links
 * - 'link': External URL
 * - 'screen': Screen navigation (internal screen ID)
 */
export const LINK_TYPE_OPTIONS = [
  { value: 'link', label: 'link' },
  { value: 'screen', label: 'screen' },
] as const;

/**
 * Image Kind options from ImageKind enum
 * Controls image positioning relative to content
 */
export const IMAGE_KIND_OPTIONS = [
  { value: 'ImageKind1', label: 'Image till navbar' },
  { value: 'ImageKind2', label: 'Image till top' },
  { value: 'ImageKind3', label: 'Image after text' },
  { value: 'ImageKind4', label: 'Between title/subtitle' },
] as const;

/**
 * Number options 0-500 (step 10) for image width/height
 */
export const NUMBER_0_500_OPTIONS = Array.from({ length: 51 }, (_, i) => ({
  value: String(i * 10),
  label: String(i * 10),
}));

/**
 * Media Top Alignment options from MediaTopAlignment enum
 * Controls media positioning relative to screen elements
 */
export const TOP_ALIGNMENT_OPTIONS = [
  { value: 'top', label: 'Top' },
  { value: 'safeArea', label: 'Safe Area' },
  { value: 'navigationBar', label: 'Navigation Bar' },
] as const;

/**
 * Number options 0-100 for heightPercentage
 */
export const NUMBER_0_100_OPTIONS = Array.from({ length: 101 }, (_, i) => ({
  value: String(i),
  label: String(i),
}));

/**
 * Paywall Image Vertical Position options
 * Controls where the media image is positioned relative to paywall content
 * From PaywallImageVerticalPositionKind enum (subset of values)
 */
export const IMAGE_VERTICAL_POSITION_OPTIONS = [
  { value: 'HeaderTop', label: 'Header Top' },
  { value: 'HeaderBottom', label: 'Header Bottom' },
] as const;

/**
 * Button Visibility options from ButtonVisibility enum
 * Controls button visibility based on user input state
 * Used for footer buttons on input screens (field, picker, calendar)
 */
export const BUTTON_VISIBILITY_OPTIONS = [
  { value: 'default', label: 'Default (Always Visible)' },
  { value: 'disabledUntilValueEntered', label: 'Disabled Until Value Entered' },
  { value: 'hiddenUntilValueEntered', label: 'Hidden Until Value Entered' },
] as const;

/**
 * Icon placement options for TapTargetIconPlacement enum
 * Controls where the icon is positioned relative to button text
 */
export const ICON_PLACEMENT_OPTIONS = [
  { value: 'start', label: 'Start (before text)' },
  { value: 'end', label: 'End (after text)' },
  { value: 'fixedStart', label: 'Fixed Start (pinned left)' },
  { value: 'fixedEnd', label: 'Fixed End (pinned right)' },
  { value: 'top', label: 'Top (above text)' },
  { value: 'bottom', label: 'Bottom (below text)' },
] as const;

/**
 * Paywall Footer Elements Order options from PaywallFooterElementsOrder enum
 * Controls whether the Purchase button or Auto-Renew label renders first
 */
export const PAYWALL_FOOTER_ELEMENTS_ORDER_OPTIONS = [
  { value: 'PurchaseFirst', label: 'PurchaseFirst' },
  { value: 'AutoRenewLabelFirst', label: 'AutoRenewLabelFirst' },
] as const;
